import React, { useState, useEffect, useCallback } from 'react';
import { useDisclosure, DisclosureModal, DisclosurePersistentBanner } from './DisclosureBanner';
import { BiometricGate, useBiometricGate } from './BiometricGate';
import { SignalRating, useBriefingRatings } from './components/SignalRating';
import { PEWidget } from './components/PEWidget';
import { AudioPlayer } from './components/AudioPlayer';
import SubscriberDashboard from './SubscriberDashboard';
import FeedbackButton from './components/FeedbackButton';
import { useAnalytics } from './hooks/useAnalytics';
import { SourceListInline } from './components/SourceList';
import { useDemographics, DemographicsModal } from './DemographicsModal';
import './App.css';
import InvitationPage from './components/InvitationPage';
import AdminDashboard from './AdminDashboard';
import AdminBar from './AdminBar';
import StripePaymentModal from './components/StripePaymentModal';
import GlobalView from './components/GlobalInterface/GlobalView';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';

const AuthPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = isSignup ? `${API_URL}/api/auth/signup` : `${API_URL}/api/auth/login`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const text = await response.text();
      const data = JSON.parse(text);
      if (response.ok) {
        localStorage.setItem('token', data.token);
        onLogin(data.token);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 style={{ fontFamily: "var(--font-display)" }}>Bearing Intelligence</h1>
        <p className="auth-tagline">Know your bearing.</p>
        <form onSubmit={handleSubmit}>
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          <button type="submit">{isSignup ? 'Create Account' : 'Sign In'}</button>
        </form>
        <p className="auth-toggle">
          {isSignup ? 'Have an account?' : "Don't have an account?"}
          <button type="button" onClick={() => setIsSignup(!isSignup)} className="link-button">
            {isSignup ? 'Sign in' : 'Sign up'}
          </button>
        </p>
        {error && <p className="error">{error}</p>}
      </div>
    </div>
  );
};

const BriefingApp = ({ token, onLogout, onAdminStatus, viewAsSubscriber }) => {
  const { track } = useAnalytics();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setShowInstallBanner(false);
    setInstallPrompt(null);
  };
  const { accepted, accept } = useDisclosure();
  const { submitted, markSubmitted } = useDemographics();
  const [briefing, setBriefing] = useState(null);
  const [sundayEdition, setSundayEdition] = useState(null);
  const [todayEvents, setTodayEvents] = useState([]);
  const [hypotheses, setHypotheses] = useState([]);
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('today');
  const [error, setError] = useState('');
  const [userIsPremium, setUserIsPremium] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const checkSubscription = useCallback(async (currentToken) => {
    try {
      const response = await fetch(API_URL + '/api/user/status', {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserIsPremium(data.isPremium);
        setIsAdmin(data.isAdmin || false);
        setUserEmail(data.email || '');
        onAdminStatus(data.isAdmin || false);
      } else {
        setUserIsPremium(false);
        setIsAdmin(false);
        onAdminStatus(false);
      }
    } catch (err) {
      setUserIsPremium(false);
      setIsAdmin(false);
      onAdminStatus(false);
    } finally {
      setCheckingSubscription(false);
    }
  }, [onAdminStatus]);

  const registerNotifications = useCallback(async (currentToken) => {
    if (!('serviceWorker' in navigator)) return;
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY
      });
      await fetch(API_URL + '/api/notifications/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${currentToken}` },
        body: JSON.stringify({ subscription: sub })
      });
    } catch (err) {
      console.error('Notifications failed:', err);
    }
  }, []);

  const fetchBriefing = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL + '/api/briefings/today', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setBriefing(await response.json());
      } else if (response.status === 404) {
        setError('Briefing not available yet. Check back at 0100 GMT.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchSundayEdition = useCallback(async () => {
    try {
      const response = await fetch(API_URL + '/api/sunday-editions/latest');
      if (response.ok) {
        const data = await response.json();
        setSundayEdition(data.edition);
      }
    } catch (err) {}
  }, []);

  const fetchHypotheses = useCallback(async () => {
    try {
      if (!briefing?.id) return;
      const response = await fetch(`${API_URL}/api/hypotheses/${briefing?.id}`);
      if (response.ok) {
        const data = await response.json();
        setHypotheses(data.hypotheses || []);
      }
    } catch (err) {}
  }, [briefing?.id]);

  const fetchTodayEvents = useCallback(async () => {
    try {
      const response = await fetch(API_URL + '/api/v2/global/events?days=1', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTodayEvents(data.events || []);
      }
    } catch (err) {}
  }, [token]);

  const fetchArchive = useCallback(async () => {
    try {
      const response = await fetch(API_URL + '/api/briefings/archive', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setArchive(await response.json());
      }
    } catch (err) {
      setError(err.message);
    }
  }, [token]);

  const requestCheckout = useCallback(() => {
    setShowPaymentModal(true);
  }, []);

  const handlePaymentSuccess = useCallback(() => {
    setShowPaymentModal(false);
    setUserIsPremium(true);
    // Wait a moment for webhook to update database, then refresh subscription status
    setTimeout(() => {
      checkSubscription(token);
    }, 2000);
  }, [token, checkSubscription]);


  useEffect(() => {
    if (token) {
      checkSubscription(token);
      registerNotifications(token);
    }
  }, [token, checkSubscription, registerNotifications]);

  useEffect(() => {
    if (!checkingSubscription && (userIsPremium || isAdmin)) {
      fetchBriefing();
      fetchSundayEdition();
      fetchTodayEvents();
      fetchHypotheses();
    }
  }, [checkingSubscription, userIsPremium, isAdmin, fetchBriefing, fetchSundayEdition, fetchTodayEvents, fetchHypotheses]);

  // Poll for briefing updates every 5 minutes — picks up audio, new publications
  useEffect(() => {
    if (!userIsPremium && !isAdmin) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/briefings/today`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (!res.ok) return;
        const data = await res.json();
        // Only update if something changed (audio added, status changed)
        setBriefing(prev => {
          if (!prev || !data.briefing) return prev;
          if (prev.audio_url !== data.briefing.audio_url ||
              prev.status !== data.briefing.status) {
            return data.briefing;
          }
          return prev;
        });
      } catch(e) {}
    }, 5 * 60 * 1000); // every 5 minutes
    return () => clearInterval(interval);
  }, [userIsPremium, isAdmin, token]);

  // Fetch Sunday Edition on mount — public endpoint, no auth needed
  useEffect(() => {
    fetchSundayEdition();
  }, [fetchSundayEdition]);

  // Loading
  if (checkingSubscription) {
    return (
      <div className="app-container">
        <header className="app-header">
          <div className="header-left">
            <img src="/BI_logo.png" alt="Bearing Intelligence" className="app-logo" />
            <div className="header-text"><h1>Bearing Intelligence</h1><p>Know your bearing.</p></div>
          </div>
          <button className="logout-btn" onClick={onLogout}>Logout</button>
        </header>
        <FeedbackButton token={token} />

      <main className="app-main"><div className="loading">Checking access...</div></main>
      </div>
    );
  }

  // Admin: only redirect if NOT in subscriber preview mode
  if (isAdmin && !viewAsSubscriber) {
    window.location.href = '/admin';
    return null;
  }

  // Paywall: non-premium, non-admin users
  if (!userIsPremium && !isAdmin) {
    return (
      <div className="app-container">
        <header className="app-header">
          <div className="header-left">
            <img src="/BI_logo.png" alt="Bearing Intelligence" className="app-logo" />
            <div className="header-text"><h1>Bearing Intelligence</h1><p>Know your bearing.</p></div>
          </div>
          <button className="logout-btn" onClick={onLogout}>Logout</button>
        </header>
        <FeedbackButton token={token} />

      <main className="app-main">
          <div style={{padding: '40px', backgroundColor: '#1a1f2e', borderRadius: '8px', marginTop: '40px', textAlign: 'center', maxWidth: '600px', margin: '40px auto'}}>
            <h2 style={{color: '#c4a563', fontSize: '32px', marginBottom: '20px'}}>Daily Briefing</h2>
            <p style={{fontSize: '18px', marginBottom: '30px', color: '#e8e9ed'}}>Subscribe to receive daily geopolitical intelligence briefings.</p>
            <p style={{fontSize: '48px', fontWeight: 'bold', color: '#c4a563', marginBottom: '30px'}}>$199<span style={{fontSize: '24px'}}>/month</span></p>
            <button onClick={requestCheckout} style={{padding: '15px 40px', backgroundColor: '#c4a563', color: '#0f1419', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '18px'}}>
              Subscribe Now
            </button>
          </div>
        </main>
        {showPaymentModal && (
          <StripePaymentModal 
            onSuccess={handlePaymentSuccess}
            onClose={() => setShowPaymentModal(false)}
            email={userEmail}
          />
        )}
      </div>
    );
  }

  // Premium / admin subscriber preview
  return (
    <div className="app-container">
      {showInstallBanner && (
        <div style={{
          position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)',
          zIndex:490, width:'calc(100% - 32px)', maxWidth:480,
          backgroundColor:'#141b27', border:'1px solid rgba(196,165,99,0.3)',
          borderRadius:8, padding:'12px 16px',
          boxShadow:'0 4px 24px rgba(0,0,0,0.6)',
          display:'flex', alignItems:'center', gap:12,
          fontFamily:"'Inter', sans-serif"
        }}>
          <img src="/BI_logo.png" alt="" style={{ width:32, height:32, borderRadius:6 }} />
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ color:'#e2e8f0', fontSize:13, fontWeight:600, marginBottom:2 }}>Add to Home Screen</div>
            <div style={{ color:'#4a5568', fontSize:11, lineHeight:1.4 }}>Install Bearing Intelligence for the best experience</div>
          </div>
          <div style={{ display:'flex', gap:8, flexShrink:0 }}>
            <button onClick={() => setShowInstallBanner(false)} style={{ background:'none', border:'1px solid #1e2a3a', borderRadius:4, color:'#4a5568', fontSize:11, padding:'5px 8px', cursor:'pointer', fontFamily:'inherit' }}>Later</button>
            <button onClick={handleInstall} style={{ backgroundColor:'#c4a563', border:'none', borderRadius:4, color:'#090d14', fontSize:11, fontWeight:600, padding:'5px 10px', cursor:'pointer', fontFamily:'inherit' }}>Install</button>
          </div>
        </div>
      )}
      {!accepted && <DisclosureModal onAccept={accept} />}
      {accepted && !submitted && <DemographicsModal onComplete={markSubmitted} />}
      {/* BIOMETRICS DISABLED — enable for beta */}
      {false && accepted && submitted && window.__biometricAvailable && localStorage.getItem('bi_biometric_enrolled') !== 'true' && (
        <BiometricGate onVerified={() => {}} onSkip={() => {}} />
      )}
      <DisclosurePersistentBanner />
      <header className="app-header">
        <div className="header-left">
          <img src="/BI_logo.png" alt="Bearing Intelligence" className="app-logo" />
          <div className="header-text"><h1>Bearing Intelligence</h1><p>Know your bearing.</p></div>
        </div>
        <button className="logout-btn" onClick={onLogout}>Logout</button>
      </header>
      <nav className="app-nav">
        <button className={`nav-btn ${view === 'today' ? 'active' : ''}`} onClick={() => { setView('today'); track('briefing_opened'); }}>Today's Briefing</button>
        <button className={`nav-btn ${view === 'global' ? 'active' : ''}`} onClick={() => { setView('global'); track('map_opened'); }}>Global Bearings</button>
        <button className={`nav-btn ${view === 'dashboard' ? 'active' : ''}`} onClick={() => { setView('dashboard'); track('dashboard_visited'); }}>My Dashboard</button>
      </nav>
      <FeedbackButton token={token} />

      <main className="app-main">
        {view === 'today' ? (
          <TodayView briefing={briefing} loading={loading} error={error} onRefresh={fetchBriefing} sundayEdition={sundayEdition} todayEvents={todayEvents} hypotheses={hypotheses} />
        ) : view === 'global' ? (
          <GlobalView />
        ) : view === 'dashboard' ? (
          <SubscriberDashboard archive={archive} fetchArchive={fetchArchive} />
        ) : null}
      </main>
    </div>
  );
};

const TodayView = ({ briefing, loading, error, onRefresh, sundayEdition, todayEvents, hypotheses }) => {
  const { ratings, myRatings } = useBriefingRatings(briefing?.id);
  if (loading) return <div className="loading">Loading today's briefing...</div>;
  if (error) return <div className="error-container"><p>{error}</p><button onClick={onRefresh}>Try again</button></div>;
  if (!briefing) return <div>No briefing available</div>;

  // Parse JSON fields if they're strings
  const parseField = (field) => {
    if (!field) return null;
    if (typeof field === 'string') {
      try {
        return JSON.parse(field);
      } catch {
        return field;
      }
    }
    return field;
  };

  const keyDevelopments = parseField(briefing.key_developments) || [];
  const strategicPattern = parseField(briefing.strategic_pattern) || {};
  const sectorWatch = parseField(briefing.sector_watch) || {};
  const implications = parseField(briefing.implications) || {};
  const monitoringPriorities = parseField(briefing.monitoring_priorities) || [];

  const parse = (val) => { try { return typeof val==='string' ? JSON.parse(val) : val; } catch { return val||{}; } };
  const sundayExec = sundayEdition ? (parse(sundayEdition.executive_summary) || {}) : null;

  return (
    <div>
      {sundayEdition && sundayExec && new Date().getDay() === 0 && (
        <div style={{ maxWidth:860, margin:'0 auto 0', padding:'20px 16px 0' }}>
          <SundayEditionCard item={{
            ...sundayEdition,
            entry_type:'sunday',
            date: sundayEdition.week_ending
          }} />
        </div>
      )}
    <article className="briefing-article">
      <header className="briefing-header">
        <h1 style={{ fontFamily: "var(--font-display)" }}>Daily Briefing</h1>
        <p className="briefing-date">{new Date().toLocaleDateString('en-US', {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})}</p>
      </header>
      {briefing.audio_url && <AudioPlayer audioUrl={briefing.audio_url} title="Daily Briefing" />}
      <section className="briefing-section">
        <h2>Executive Summary</h2>
        <p className="summary">{briefing.executive_summary}</p>
      </section>
      <section className="briefing-section">
        <h2>Key Developments</h2>
        <div className="developments">
          {Array.isArray(keyDevelopments) && keyDevelopments.length > 0 ? (
            keyDevelopments.map((dev, idx) => {
              // Match event by title similarity for CI data
              const matchedEvent = todayEvents.find(e =>
                e.title && dev.headline && e.title.substring(0,40) === dev.headline.substring(0,40)
              );
              const ci = matchedEvent ? Math.round(matchedEvent.confidence_level * 100) : null;
              const ciColor = ci >= 80 ? '#ff9933' : ci >= 60 ? '#c4a563' : '#8a9bbf';
              const domainColor = dev.domain === 'MILITARY' ? '#ff5555' :
                dev.domain === 'ECONOMIC' ? '#c4a563' :
                dev.domain === 'POLITICAL' || dev.domain === 'DIPLOMATIC' ? '#66aaff' :
                dev.domain === 'SOCIAL' ? '#81c784' : '#aa88cc';

              // Highlight CI% patterns in assessment text
              const highlightCI = (text) => {
                if (!text) return text;
                const parts = text.split(/(CI\s*\d+%|confidence interval of \d+%|\d+%\s*confidence)/gi);
                return parts.map((part, i) =>
                  /CI\s*\d+%|confidence interval|\d+%\s*confidence/i.test(part)
                    ? <span key={i} style={{ color:'#c4a563', fontWeight:'bold', backgroundColor:'rgba(196,165,99,0.1)', padding:'0 3px', borderRadius:3 }}>{part}</span>
                    : part
                );
              };

              return (
                <div key={idx} className="development" style={{ position:'relative' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8, marginBottom:4 }}>
                    <h3 style={{ margin:0, flex:1 }}>{dev.headline || dev.title || `Development ${idx + 1}`}</h3>
                    <div style={{ display:'flex', gap:6, flexShrink:0, alignItems:'center' }}>
                      {dev.domain && (
                        <span style={{ fontSize:10, padding:'2px 6px', borderRadius:3, fontWeight:'bold',
                          backgroundColor: domainColor+'22', color:domainColor,
                          border:`1px solid ${domainColor}55`, textTransform:'uppercase', letterSpacing:'0.4px' }}>
                          {dev.domain}
                        </span>
                      )}
                      {ci && (
                        <span style={{ fontSize:10, padding:'2px 6px', borderRadius:3, fontWeight:'bold',
                          backgroundColor: ciColor+'22', color:ciColor,
                          border:`1px solid ${ciColor}55`, letterSpacing:'0.3px' }}>
                          CI {ci}%
                        </span>
                      )}
                    </div>
                  </div>
                  <p>{highlightCI(dev.assessment || dev.fact || (typeof dev === 'string' ? dev : ''))}</p>
                  {dev.implications && (
                    <div style={{ borderLeft:'2px solid rgba(138,155,191,0.2)', paddingLeft:10, marginTop:6 }}>
                      <p style={{ color:'#8a9bbf', fontSize:'14px', fontStyle:'italic', margin:'0 0 6px 0' }}>
                        <strong style={{ color:'#8a9bbf' }}>Implications:</strong> {dev.implications}
                      </p>
                      {(() => {
                        const h = hypotheses.find(h => h.development_idx === idx && !h.is_meta);
                        return h ? (
                          <PEWidget
                            hypothesisId={h.id}
                            hypothesis={h.hypothesis}
                            timeframeDays={h.timeframe_days}
                            timeframeBasis={h.timeframe_basis}
                            forwardPE={h.forward_pe}
                            peNarrative={h.pe_narrative}
                            baseRate={h.base_rate}
                            baseRateBasis={h.base_rate_basis}
                            sourceReliability={h.source_reliability}
                            sourceCredibility={h.source_credibility}
                            sourceBasis={h.source_basis}
                            briefingDate={briefing.date}
                          />
                        ) : dev.probabilistic_assessment ? (
                          <PEWidget
                            hypothesisId={null}
                            hypothesis={dev.probabilistic_assessment.hypothesis}
                            timeframeDays={dev.probabilistic_assessment.timeframe_days}
                            timeframeBasis={dev.probabilistic_assessment.timeframe_basis}
                            forwardPE={dev.probabilistic_assessment.forward_pe}
                            peNarrative={dev.probabilistic_assessment.pe_narrative}
                            baseRate={dev.probabilistic_assessment.base_rate}
                            baseRateBasis={dev.probabilistic_assessment.base_rate_basis}
                            sourceReliability={dev.source_assessment?.reliability}
                            sourceCredibility={dev.source_assessment?.credibility}
                            sourceBasis={dev.source_assessment?.basis}
                            briefingDate={briefing.date}
                          />
                        ) : null;
                      })()}
                    </div>
                  )}
                  <SourceListInline briefingId={briefing.id} devIdx={idx} headline={dev.headline} />

                </div>
              );
            })
          ) : (
            <p style={{ color: '#8a8b8f', fontStyle: 'italic' }}>No developments recorded</p>
          )}
        </div>
      </section>
      <section className="briefing-section">
        <h2>Strategic Pattern</h2>
        {strategicPattern && (typeof strategicPattern === 'object') ? (
          <div style={{ background: 'rgba(196,165,99,0.05)', padding: '16px', borderLeft: '3px solid #c4a563', borderRadius: '4px' }}>
            {strategicPattern.observation && (
              <div style={{ marginBottom: '12px' }}>
                <p style={{ margin: '0 0 8px 0', color: '#c4a563', fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Observation</p>
                <p style={{ margin: 0, color: '#e8e9ed', lineHeight: '1.7' }}>{strategicPattern.observation}</p>
              </div>
            )}
            {strategicPattern.significance && (
              <div>
                <p style={{ margin: '0 0 8px 0', color: '#c4a563', fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Significance</p>
                <p style={{ margin: '0 0 8px 0', color: '#e8e9ed', lineHeight: '1.7' }}>{strategicPattern.significance}</p>
                {(() => {
                  const metaH = hypotheses.find(h => h.is_meta);
                  return metaH ? (
                    <PEWidget
                      hypothesisId={metaH.id}
                      hypothesis={metaH.hypothesis}
                      timeframeDays={metaH.timeframe_days}
                      timeframeBasis={metaH.timeframe_basis}
                      forwardPE={metaH.forward_pe}
                      peNarrative={metaH.pe_narrative}
                      baseRate={metaH.base_rate}
                      baseRateBasis={metaH.base_rate_basis}
                      briefingDate={briefing.date}
                    />
                  ) : strategicPattern.cross_development_synthesis ? (
                    <PEWidget
                      hypothesisId={null}
                      hypothesis={strategicPattern.cross_development_synthesis.meta_hypothesis}
                      timeframeDays={strategicPattern.cross_development_synthesis.timeframe_days}
                      timeframeBasis={strategicPattern.cross_development_synthesis.timeframe_basis}
                      forwardPE={strategicPattern.cross_development_synthesis.forward_pe}
                      peNarrative={strategicPattern.cross_development_synthesis.pe_narrative}
                      baseRate={strategicPattern.cross_development_synthesis.base_rate}
                      baseRateBasis={strategicPattern.cross_development_synthesis.base_rate_basis}
                      briefingDate={briefing.date}
                    />
                  ) : null;
                })()}
              </div>
            )}
          </div>
        ) : strategicPattern ? (
          <p style={{ color: '#e8e9ed', lineHeight: '1.7' }}>{typeof strategicPattern === 'string' ? strategicPattern : JSON.stringify(strategicPattern)}</p>
        ) : (
          <p style={{ color: '#8a8b8f', fontStyle: 'italic' }}>No strategic pattern identified</p>
        )}

      </section>
      <section className="briefing-section">
        <h2>Sector Watch</h2>
        <div className="implications-grid">
          {sectorWatch && Object.keys(sectorWatch).length > 0 ? (
            Object.entries(sectorWatch).map(([sector, text]) => (
              <div key={sector} className="implication-card">
                <h3>{sector}</h3>
                <p>{text}</p>
                <SignalRating briefingId={briefing.id} sectionKey={`sector_${sector.toLowerCase().replace(/\s+/g,'_')}`}
                  initialRating={myRatings[`sector_${sector.toLowerCase().replace(/\s+/g,'_')}`]}
                  initialAggregate={ratings[`sector_${sector.toLowerCase().replace(/\s+/g,'_')}`]} />
              </div>
            ))
          ) : implications && Object.keys(implications).length > 0 ? (
            Object.entries(implications).map(([sector, text]) => (
              <div key={sector} className="implication-card">
                <h3>{sector}</h3>
                <p>{text}</p>
                <SignalRating briefingId={briefing.id} sectionKey={`sector_${sector.toLowerCase().replace(/\s+/g,'_')}`}
                  initialRating={myRatings[`sector_${sector.toLowerCase().replace(/\s+/g,'_')}`]}
                  initialAggregate={ratings[`sector_${sector.toLowerCase().replace(/\s+/g,'_')}`]} />
              </div>
            ))
          ) : (
            <p style={{ color: '#8a8b8f', fontStyle: 'italic' }}>No sector watch data</p>
          )}
        </div>
      </section>
      <section className="briefing-section">
        <h2>Monitor Next 72 Hours</h2>
        {Array.isArray(monitoringPriorities) && monitoringPriorities.length > 0 ? (
          <ul>{monitoringPriorities.map((item, i) => <li key={i}>{item}</li>)}</ul>
        ) : briefing.what_to_watch ? (
          <p>{briefing.what_to_watch}</p>
        ) : (
          <p style={{ color: '#8a8b8f', fontStyle: 'italic' }}>No priorities defined</p>
        )}
      </section>
    </article>
    </div>
  );
};

const SundayEditionCard = ({ item }) => {
  const [open, setOpen] = React.useState(false);
  const parse = (val) => {
    try { return typeof val === 'string' ? JSON.parse(val) : val; } catch { return val || {}; }
  };
  const execSum = parse(item.executive_summary) || {};
  const pmesii  = parse(item.operational_environment) || {};
  const coas    = parse(item.courses_of_action) || [];
  const changes = parse(item.week_over_week_changes) || [];
  const trend   = execSum.overall_threat_trend;
  const trendColor = trend === 'escalating' ? '#ff5555' : trend === 'de-escalating' ? '#44aa66' : '#c4a563';
  const weekLabel = `${new Date(item.week_starting).toLocaleDateString('en-US', { month:'short', day:'numeric' })} – ${new Date(item.week_ending).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })}`;

  return (
    <div style={{ backgroundColor:'#1a1f2e', border:'1px solid rgba(196,165,99,0.35)', borderRadius:8, marginBottom:12, overflow:'hidden' }}>
      <div style={{ padding:'14px 18px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}
        onClick={() => setOpen(o => !o)}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5, flexWrap:'wrap' }}>
            <span style={{ padding:'2px 8px', backgroundColor:'rgba(196,165,99,0.15)', color:'#c4a563', border:'1px solid rgba(196,165,99,0.4)', borderRadius:4, fontSize:10, fontWeight:'bold', letterSpacing:'0.5px' }}>SUNDAY EDITION</span>
            {trend && <span style={{ fontSize:10, padding:'2px 7px', borderRadius:4, fontWeight:'bold', backgroundColor:trendColor+'18', color:trendColor, border:`1px solid ${trendColor}55`, textTransform:'uppercase', letterSpacing:'0.5px' }}>{trend}</span>}
          </div>
          <div style={{ color:'#e8e9ed', fontFamily:'var(--font-display)', fontSize:16, fontWeight:'bold' }}>Week of {weekLabel}</div>
        </div>
        <span style={{ color:'#555', fontSize:12, flexShrink:0, marginLeft:8 }}>{open ? '▲' : '▼'}</span>
      </div>

      {/* Key judgments preview */}
      {Array.isArray(execSum.key_judgments) && (
        <div style={{ padding:'0 18px 12px' }}>
          {(open ? execSum.key_judgments : execSum.key_judgments.slice(0,2)).map((j,i) => (
            <div key={i} style={{ display:'flex', gap:9, marginBottom:6, padding:'6px 9px', backgroundColor:'rgba(196,165,99,0.04)', borderLeft:'2px solid rgba(196,165,99,0.25)', borderRadius:'0 4px 4px 0' }}>
              <span style={{ color:'#c4a563', fontWeight:'bold', flexShrink:0, fontSize:12 }}>{i+1}.</span>
              <span style={{ color:'#c8c9cd', fontSize:13, lineHeight:1.6 }}>{j}</span>
            </div>
          ))}
          {!open && execSum.key_judgments.length > 2 && (
            <div style={{ color:'#555', fontSize:12, paddingLeft:10 }}>+{execSum.key_judgments.length - 2} more…</div>
          )}
        </div>
      )}

      {open && (
        <div style={{ borderTop:'1px solid rgba(196,165,99,0.1)', padding:'14px 18px' }}>
          {execSum.week_summary && (
            <div style={{ marginBottom:16 }}>
              <div style={{ color:'#555', fontSize:10, fontWeight:'bold', letterSpacing:'0.6px', marginBottom:7 }}>WEEK SUMMARY</div>
              <p style={{ color:'#8a8b8f', fontSize:13, lineHeight:1.7, margin:0 }}>{execSum.week_summary}</p>
            </div>
          )}
          {Array.isArray(changes) && changes.length > 0 && (
            <div style={{ marginBottom:16 }}>
              <div style={{ color:'#555', fontSize:10, fontWeight:'bold', letterSpacing:'0.6px', marginBottom:7 }}>WEEK-OVER-WEEK</div>
              {changes.map((c,i) => (
                <div key={i} style={{ padding:'7px 9px', backgroundColor:'#0f1419', borderRadius:6, marginBottom:5, border:'1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ color:'#e8e9ed', fontWeight:'bold', fontSize:12, marginBottom:2 }}>{c.topic}</div>
                  <div style={{ fontSize:11, color:'#555', display:'flex', gap:7, flexWrap:'wrap' }}>
                    <span>{c.previous}</span><span>→</span><span style={{ color:'#c8c9cd' }}>{c.current}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {pmesii && Object.keys(pmesii).length > 0 && (
            <div style={{ marginBottom:16 }}>
              <div style={{ color:'#555', fontSize:10, fontWeight:'bold', letterSpacing:'0.6px', marginBottom:7 }}>PMESII</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:5 }}>
                {Object.entries(pmesii).map(([domain,data]) => (
                  <div key={domain} style={{ padding:'8px 10px', backgroundColor:'#0f1419', borderRadius:6, border:'1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ color:'#c4a563', fontSize:10, fontWeight:'bold', textTransform:'uppercase', marginBottom:3 }}>{domain}</div>
                    <div style={{ color:'#666', fontSize:11, lineHeight:1.5 }}>{data?.assessment || String(data)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(coas) && coas.length > 0 && (
            <div>
              <div style={{ color:'#555', fontSize:10, fontWeight:'bold', letterSpacing:'0.6px', marginBottom:7 }}>COURSES OF ACTION</div>
              {coas.map((coa,i) => (
                <div key={i} style={{ padding:'8px 10px', backgroundColor:'#0f1419', borderRadius:6, marginBottom:5, border:'1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:3 }}>
                    <div style={{ color:'#e8e9ed', fontWeight:'bold', fontSize:12 }}>{coa.coa}</div>
                    <span style={{ fontSize:10, padding:'1px 6px', borderRadius:4, flexShrink:0, marginLeft:8, fontWeight:'bold', textTransform:'uppercase',
                      backgroundColor: coa.probability==='high'?'rgba(255,51,51,0.1)':coa.probability==='medium'?'rgba(196,165,99,0.1)':'rgba(68,170,102,0.1)',
                      color: coa.probability==='high'?'#ff5555':coa.probability==='medium'?'#c4a563':'#44aa66',
                      border:'1px solid currentColor' }}>{coa.probability}</span>
                  </div>
                  <div style={{ color:'#666', fontSize:11, lineHeight:1.5 }}>{coa.description}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [biometricVerified, setBiometricVerified] = useState(false);
  useBiometricGate(); // initializes async platform authenticator check
  const [isAdmin, setIsAdmin] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);

  // Read from sessionStorage so the value survives page reloads
  const [viewAsSubscriber, setViewAsSubscriber] = useState(
    () => sessionStorage.getItem('viewAsSubscriber') === 'true'
  );

  // Helper: always write to both state AND sessionStorage together
  const setSubscriberView = (val) => {
    sessionStorage.setItem('viewAsSubscriber', val ? 'true' : 'false');
    setViewAsSubscriber(val);
  };

  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/\/invite\/(.+)/);
    if (match) setInviteToken(match[1]);
    // Landing on /admin always exits subscriber preview
    if (path === '/admin') {
      sessionStorage.setItem('viewAsSubscriber', 'false');
      setViewAsSubscriber(false);
    }
  }, []);

  const handleLogin = (newToken) => { setToken(newToken); setInviteToken(null); setBiometricVerified(true); };

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('viewAsSubscriber');
    setToken(null);
    setIsAdmin(false);
    setViewAsSubscriber(false);
  };

  if (inviteToken) return <InvitationPage token={inviteToken} onSuccess={handleLogin} />;

  // Biometric gate — show when token exists but biometric not yet verified this session
  // BIOMETRICS DISABLED — enable for beta: check window.__biometricAvailable && enrolled
  if (false && token && !biometricVerified && window.__biometricAvailable && localStorage.getItem('bi_biometric_enrolled') === 'true') {
    return (
      <BiometricGate
        onVerified={() => setBiometricVerified(true)}
        onSkip={() => setBiometricVerified(true)}
      />
    );
  }

  // Admin dashboard view
  if (window.location.pathname === '/admin' && token) return (
    <>
      <AdminBar onToggle={() => {
        setSubscriberView(true);        // persisted to sessionStorage BEFORE reload
        window.location.href = '/';
      }} />
      <div style={{ paddingTop: 36 }}>
        <AdminDashboard token={token} />
      </div>
    </>
  );

  // Subscriber view (with admin bar if admin)
  return (
    <>
      {isAdmin && (
        <AdminBar onToggle={() => {
          setSubscriberView(false);     // persisted to sessionStorage BEFORE reload
          window.location.href = '/admin';
        }} />
      )}
      <div style={{ paddingTop: isAdmin ? 36 : 0 }}>
        {token
          ? <BriefingApp
              token={token}
              onLogout={handleLogout}
              onAdminStatus={setIsAdmin}
              viewAsSubscriber={viewAsSubscriber}
            />
          : <AuthPage onLogin={handleLogin} />
        }
      </div>
    </>
  );
}